/**
 * TrabalhadoresTotais
 *
 * Exibe o totalizador de trabalhadores por plano/versão para uma assistência,
 * agrupando quantidades e valores de provisão por segmento.
 *
 * ─────────────────────────────────────────────────────────────────
 * REGRAS DE AUTENTICAÇÃO
 * ─────────────────────────────────────────────────────────────────
 * - A tela exige sessão ativa (Bearer Token). `core.isAuth()` redireciona
 *   ao login caso a sessão esteja expirada.
 * - A listagem de favorecidos usa o Bearer Token da aplicação (manager-cob).
 * - As chamadas ao integrar-api (busca principal) usam Basic Auth (`getBasicAuthHeaders`).
 *
 * ─────────────────────────────────────────────────────────────────
 * FLUXO 1 — USUÁRIO COM ASSISTÊNCIA VINCULADA (beneficioId presente)
 * ─────────────────────────────────────────────────────────────────
 * Quando a rota `/empresa/relatorios/relacao-assistencia` retorna o campo
 * `assistencia` preenchido, o usuário já possui uma assistência vinculada.
 *
 * Comportamento:
 *   1. O formulário fica válido com apenas: Mês + Favorecido.
 *   2. A busca principal envia o parâmetro `assistencia` (em vez de `favorecido`).
 *
 * Params enviados ao integrar-api:
 *   GET /assistencias/trabalhadores-totais?mes=MM-YYYY&assistencia=Y
 *
 * ─────────────────────────────────────────────────────────────────
 * FLUXO 2 — USUÁRIO SEM ASSISTÊNCIA VINCULADA (beneficioId ausente)
 * ─────────────────────────────────────────────────────────────────
 * O formulário fica válido com: Mês + Favorecido.
 *
 * Params enviados ao integrar-api:
 *   GET /assistencias/trabalhadores-totais?mes=MM-YYYY&favorecido=Y
 *
 * ─────────────────────────────────────────────────────────────────
 * GESTORAS
 * ─────────────────────────────────────────────────────────────────
 * A API sempre retorna dados das duas gestoras (UPS + GESTAR) juntos.
 * Não há seleção de gestora pelo usuário.
 *
 * ─────────────────────────────────────────────────────────────────
 * REGRAS DO CAMPO MÊS
 * ─────────────────────────────────────────────────────────────────
 * - Obrigatório em ambos os fluxos.
 * - Somente meses passados são permitidos (mês atual e futuros bloqueados).
 * - Formato enviado à API: MM-YYYY (ex: 03-2025).
 *
 * ─────────────────────────────────────────────────────────────────
 * RESET DE ESTADO AO TROCAR FAVORECIDO
 * ─────────────────────────────────────────────────────────────────
 * Ao alterar o campo Favorecido, os seguintes estados são limpos:
 *   - Tabela de resultados e totais
 *
 * ─────────────────────────────────────────────────────────────────
 * TRATAMENTO DE ERROS DA BUSCA PRINCIPAL
 * ─────────────────────────────────────────────────────────────────
 * - HTTP 400: exibe a mensagem retornada pelo servidor ou "Parâmetros inválidos."
 * - HTTP 500: exibe "Erro interno do servidor. Tente novamente."
 * - Outros erros HTTP: exibe a mensagem do servidor ou "Erro {status} ao buscar dados."
 * - Em caso de erro, a tabela é ocultada (`showTable = false`).
 */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Core from 'class/Core';
import {
    Card, Form, Row, Col, Select, DatePicker, Button,
    Table, Typography, Space, Spin, message, Statistic,
} from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import ptBR from 'antd/es/date-picker/locale/pt_BR';
import { useMutation, useQuery } from 'react-query';
import config, { getBasicAuthHeaders } from 'utils/configAdapter';
import { mask } from 'utils/helpers';

dayjs.locale('pt-br');

const getIntegrarUrl = () => {
    return config[process.env.NODE_ENV].url;
};

const TrabalhadoresTotais = () => {
    const { Title, Text } = Typography;
    const [form] = Form.useForm();
    const core = new Core();

    const [tableData, setTableData] = useState([]);
    const [totaisData, setTotaisData] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [formValid, setFormValid] = useState(false);

    // Valida sessão ao montar
    useEffect(() => {
        core.isAuth();
    }, []);

    // Busca lista de favorecidos e assistência do usuário (Bearer Token da aplicação)
    const { data: queryFavorecidos = { list: [], beneficioId: null }, isLoading: loadingFavorecidos } = useQuery(
        'FAVORECIDOS-TRABALHADORES-TOTAIS',
        async () => {
            const response = await fetch(
                `${core.getUrl()}api-v1/empresa/relatorios/relacao-assistencia`,
                { method: 'GET', headers: core.getHeadersFetch(true) }
            );
            const result = await core.responseConvert(response);
            return {
                list: result.content.map((el) => ({
                    value: el.enti_cd_nseq,
                    label: el.enti_ds_sigla,
                })),
                // Quando assistencia vier preenchida, usa-a diretamente (sem exigir favorecido)
                beneficioId: result.assistencia ?? null,
            };
        },
        { onError: (err) => core.catchFecth(err) }
    );

    const favorecidos = queryFavorecidos.list;
    const beneficioId = queryFavorecidos.beneficioId;

    // Requisição principal ao integrar-api (Basic Auth)
    const { mutate: executeBusca, isLoading: loadingBusca } = useMutation(
        async (params) => {
            const queryParams = new URLSearchParams({
                mes: params.mes,
                ...(params.assistencia ? { assistencia: params.assistencia } : { favorecido: params.favorecido }),
            });

            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/trabalhadores-totais?${queryParams}`,
                { method: 'GET', headers: getBasicAuthHeaders() }
            );

            if (!response.ok) {
                // Tenta extrair mensagem de erro do corpo da resposta
                let errorMsg;
                try {
                    const errorBody = await response.json();
                    errorMsg = errorBody.message || errorBody.detail || null;
                } catch (_) {
                    errorMsg = null;
                }

                if (response.status === 400) {
                    throw new Error(errorMsg || 'Parâmetros inválidos.');
                }
                if (response.status === 500) {
                    throw new Error('Erro interno do servidor. Tente novamente.');
                }
                throw new Error(errorMsg || `Erro ${response.status} ao buscar dados.`);
            }

            return response.json();
        },
        {
            onSuccess: (result) => {
                setTableData(result.content || []);
                setTotaisData(result.total || null);
                setShowTable(true);
                message.success(`${(result.content || []).length} registros encontrados.`);
            },
            onError: (err) => {
                message.error(err.message || 'Erro ao buscar dados.');
                setShowTable(false);
            },
        }
    );

    const updateFormValid = (values) => {
        setFormValid(!!(values.mes && values.favorecido));
    };

    const handleFavorecidoChange = (favorecido) => {
        setShowTable(false);
        setTotaisData(null);
        setTableData([]);
        updateFormValid({ ...form.getFieldsValue(), favorecido });
    };

    const onFinish = (values) => {
        const mes = values.mes.format('MM-YYYY');
        const params = {
            mes,
            ...(beneficioId ? { assistencia: beneficioId } : { favorecido: values.favorecido }),
        };
        executeBusca(params);
    };

    // // Download Excel via fetch + blob (necessário para enviar Basic Auth header)
    // const onDownloadExcel = async () => {
    //     if (!lastParams || loadingExcel) return;
    //
    //     const queryParams = new URLSearchParams({
    //         mes: lastParams.mes,
    //         excel: 'sim',
    //         ...(lastParams.assistencia ? { assistencia: lastParams.assistencia } : { favorecido: lastParams.favorecido }),
    //     });
    //
    //     try {
    //         setLoadingExcel(true);
    //         const response = await fetch(
    //             `${getIntegrarUrl()}/assistencias/trabalhadores-totais?${queryParams}`,
    //             { method: 'GET', headers: getBasicAuthHeaders() }
    //         );
    //
    //         if (!response.ok) {
    //             message.error('Erro ao gerar o Excel.');
    //             return;
    //         }
    //
    //         const blob = await response.blob();
    //         const url = window.URL.createObjectURL(blob);
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.download = `trabalhadores-totais-${lastParams.mes}.xlsx`;
    //         document.body.appendChild(link);
    //         link.click();
    //         link.remove();
    //         window.URL.revokeObjectURL(url);
    //         message.success('Excel gerado com sucesso.');
    //     } catch (_) {
    //         message.error('Erro ao gerar o Excel.');
    //     } finally {
    //         setLoadingExcel(false);
    //     }
    // };

    const columns = [
        { title: 'UF', dataIndex: 'uf', key: 'uf', width: 60 },
        { title: 'Segmento', dataIndex: 'segmento', key: 'segmento', width: 220, ellipsis: true },
        { title: 'Plano', dataIndex: 'plano', key: 'plano', width: 300, ellipsis: true },
        { title: 'Versão', dataIndex: 'versao', key: 'versao', width: 100 },
        {
            title: 'Qtd. Trabalhadores',
            dataIndex: 'qt_trabalhadores',
            key: 'qt_trabalhadores',
            width: 150,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Vl. Provisão',
            dataIndex: 'vl_provisao',
            key: 'vl_provisao',
            width: 150,
            align: 'right',
            render: (v) => mask(v ?? 0, 'currency'),
        },
        {
            title: 'Afastados +12m',
            dataIndex: 'qt_afastados_12m',
            key: 'qt_afastados_12m',
            width: 120,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Idade Máxima',
            dataIndex: 'qt_idade_maxima',
            key: 'qt_idade_maxima',
            width: 110,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Vl. Prov. Afas. + Id. Máx.',
            dataIndex: 'vl_provisao_afastados',
            key: 'vl_provisao_afastados',
            width: 170,
            align: 'right',
            render: (v) => mask(v ?? 0, 'currency'),
        },
        {
            title: 'Lanç. Manual',
            dataIndex: 'lancamento_manual',
            key: 'lancamento_manual',
            width: 110,
            align: 'center',
            render: (v) => v === 'Sim'
                ? <span style={{ color: '#faad14', fontWeight: 600 }}>Sim</span>
                : <span style={{ color: '#999' }}>Não</span>,
        },
        {
            title: 'Baixa Zerada',
            dataIndex: 'baixa_zerada',
            key: 'baixa_zerada',
            width: 110,
            align: 'center',
            render: (v) => v === 'Sim'
                ? <span style={{ color: '#faad14', fontWeight: 600 }}>Sim</span>
                : <span style={{ color: '#999' }}>Não</span>,
        },
        {
            title: 'Devolução',
            dataIndex: 'devolucao',
            key: 'devolucao',
            width: 100,
            align: 'center',
            render: (v) => v === 'Sim'
                ? <span style={{ color: '#f5222d', fontWeight: 600 }}>Sim</span>
                : <span style={{ color: '#999' }}>Não</span>,
        },
    ];

    const loading = loadingFavorecidos || loadingBusca;

    return (
        <>
            {/* Cabeçalho */}
            <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: '20px 24px' }}>
                <Row align='middle'>
                    <Col>
                        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                            <Icons.TeamOutlined style={{ marginRight: 8 }} />
                            Totalizador de Trabalhadores por Plano
                        </Title>
                        <Text type='secondary' style={{ fontSize: '14px' }}>
                            Visualize a quantidade de trabalhadores e valores de arrecadação agrupados por plano e versão
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* Filtros */}
            <Card
                title={
                    <Space>
                        <Icons.SearchOutlined />
                        <span>Filtros de Pesquisa</span>
                    </Space>
                }
                style={{ marginBottom: 24 }}
                bodyStyle={{ padding: '20px 24px' }}
            >
                <Form
                    form={form}
                    layout='vertical'
                    onFinish={onFinish}
                    onValuesChange={(_, allValues) => {
                        updateFormValid(allValues);
                    }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name='mes'
                                label={
                                    <Space>
                                        <Icons.CalendarOutlined />
                                        <span>Mês de Referência</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Selecione o mês de referência' }]}
                            >
                                <DatePicker
                                    picker='month'
                                    locale={ptBR}
                                    format='MM/YYYY'
                                    placeholder='Selecione o mês'
                                    style={{ width: '100%' }}
                                    size='large'
                                    disabledDate={(current) => current && current >= dayjs().startOf('month')}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name='favorecido'
                                label={
                                    <Space>
                                        <Icons.BankOutlined />
                                        <span>Favorecido</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Selecione o favorecido' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder='Selecione o favorecido'
                                    options={favorecidos}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={handleFavorecidoChange}
                                    size='large'
                                    loading={loadingFavorecidos}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                            <Form.Item label=' ' colon={false}>
                                <Button
                                    type='primary'
                                    htmlType='submit'
                                    size='large'
                                    disabled={!formValid || loading}
                                    loading={loadingBusca}
                                    icon={<Icons.SearchOutlined />}
                                    style={{ width: '100%' }}
                                >
                                    BUSCAR
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Painel de totais */}
            {showTable && totaisData && (
                <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
                    <Row gutter={[16, 8]}>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Total Trabalhadores'
                                value={totaisData.qt_trabalhadores ?? 0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Afastados +12m'
                                value={totaisData.qt_afastados_12m ?? 0}
                            />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Idade Máxima'
                                value={totaisData.qt_idade_maxima ?? 0}
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Tabela */}
            {showTable && (
                <Card>
                    <Spin spinning={loadingBusca}>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            rowKey={(_, index) => `row-${index}`}
                            pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
                            scroll={{ x: 1550 }}
                            locale={{
                                emptyText: (
                                    <div style={{ textAlign: 'center' }}>
                                        <b>Nenhum registro encontrado para os filtros informados.</b>
                                    </div>
                                ),
                            }}
                        />
                    </Spin>
                </Card>
            )}

            {/* Estado vazio */}
            {!showTable && !loadingBusca && (
                <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ color: '#999' }}>
                        <Icons.BarChartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <Title level={4} type='secondary'>
                            Configure os filtros e clique em "BUSCAR"
                        </Title>
                        <Text type='secondary'>
                            Selecione o mês de referência e o favorecido para visualizar o totalizador
                        </Text>
                    </div>
                </Card>
            )}
        </>
    );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(TrabalhadoresTotais);
